import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Table,
  List,
  Button,
  Icons,
  Icon,
} from './TableOfContents.styles';
import { motion, useScroll, useTransform } from 'framer-motion';

export type LinksList = {
  id: string;
  title: string;
}[];

export interface TableOfContentsProps {
  children?: React.ReactNode;
  show?: boolean;
  linksList?: LinksList;
}

/**
 * Client side function to get all H2 headings
 * and use them as headings for the TOC
 * @param ref - the container to look in
 * @returns a link list of headings found
 */
const getHeadingsFromDOM = (ref: HTMLDivElement) => {
  const headings = Array.from(ref.querySelectorAll('h2')).filter(
    (heading) => heading.id
  );

  return headings.map((heading) => ({
    id: heading.id,
    title: heading.textContent || '',
  }));
};

/**
 * Helper function to find the closest intersecting entry
 * @param entries - all the currently observed items
 * @returns the closet observed item near the top of the viewport
 */
const getClosestToTop = (entries: IntersectionObserverEntry[]) => {
  return entries
    .filter((entry) => entry.isIntersecting)
    .reduce<IntersectionObserverEntry | null>((closest, current) => {
      if (
        !closest ||
        current.boundingClientRect.top < closest.boundingClientRect.top
      ) {
        return current;
      }
      return closest;
    }, null);
};

const TableOfContents: React.FC<TableOfContentsProps> = ({
  children,
  show = false,
  linksList,
  ...rest
}) => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const [headingDetails, setHeadingDetails] = useState<LinksList>([]);

  const { scrollYProgress } = useScroll({
    offset: ['start 30%', 'end 100%'],
    target: contentContainerRef,
  });

  const percentOpacity = useTransform(
    scrollYProgress,
    [0, 0.009, 0.995, 1],
    ['0%', '100%', '100%', '0%']
  );

  useEffect(() => {
    if (!contentContainerRef.current) return;

    const finalHeadings =
      linksList && linksList.length > 0
        ? linksList
        : getHeadingsFromDOM(contentContainerRef.current);

    setHeadingDetails(finalHeadings);
  }, [linksList]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const closestEntry = getClosestToTop(entries);

        if (closestEntry) {
          setActiveHeadingId(closestEntry.target.id);
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.5,
      }
    );

    headingDetails.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      headingDetails.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          observer.unobserve(element);
        }
      });
      observer.disconnect();
    };
  }, [headingDetails]);

  return show ? (
    <Container ref={contentContainerRef} {...rest}>
      <Table
        as={motion.div}
        style={{
          opacity: percentOpacity,
        }}
      >
        <Icons>
          {headingDetails.map((heading) => (
            <Icon
              key={`icon-${heading.id}`}
              className={activeHeadingId === heading.id ? 'active' : ''}
            />
          ))}
        </Icons>
        <List>
          {headingDetails.map((heading) => (
            <Button
              key={`item-${heading.id}`}
              className={activeHeadingId === heading.id ? 'active' : ''}
              aria-current={activeHeadingId === heading.id ? 'true' : 'false'}
              onClick={() => {
                setActiveHeadingId(heading.id);
                const target = document.getElementById(heading.id);
                if (target) {
                  target.scrollIntoView({ behavior: 'auto' });
                  target.focus({ preventScroll: true });
                }
              }}
            >
              {heading.title}
            </Button>
          ))}
        </List>
      </Table>
      {children}
    </Container>
  ) : (
    <React.Fragment>{children}</React.Fragment>
  );
};

export default TableOfContents;
